import type { FC, ChangeEvent, DragEvent } from 'react';
import { Box, Grid, MenuItem, type Palette, Typography } from '@mui/material';
import type { DocumentTypeTaxonomyEntry } from '@lio/rule-engine';
import { formatBytes } from '@utils/fileUtils';
import { TextField } from '@components/textField/TextField';
import { IconButton } from '@components/iconButton';
import { TrashAlt, FileUpload } from '@assets/svgs';
import { Button } from '@components/button';
import { FILE_TYPES } from '@components/drawerItem/DrawerItem.constants';
import FileDropWrapper from '@utils/FileDropWrapper';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { AttachmentFile } from '@components/container/AttachmentFile';
import type { FileEntry } from '@hooks/useDocumentFileSelection';

interface DocumentUploadViewProps {
  files: FileEntry[];
  dragging: boolean;
  uploadableTypes: DocumentTypeTaxonomyEntry[];
  onFileInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onUpdateEntry: (index: number, updates: Partial<Pick<FileEntry, 'documentTypeId' | 'description'>>) => void;
  onRemoveFile: (index: number) => void;
  fileInputId: string;
  totalSize: number;
}

const getDropZoneBg = (dragging: boolean, palette: Palette) => {
  if (dragging) {
    return palette.theme === 'lio' ? '#465D7733' : `${palette.background.main}10`;
  }
  return 'background.light';
};

export const DocumentUploadView: FC<DocumentUploadViewProps> = ({
  files,
  dragging,
  uploadableTypes,
  onFileInputChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onUpdateEntry,
  onRemoveFile,
  fileInputId,
  totalSize,
}) => {
  const acceptedFileTypes = FILE_TYPES.map((el) => `.${el}`).join(', ');

  return (
    <Grid size={{ xs: 11 }} sx={{ m: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2 }}>
        <Typography variant='h5'>{`${files.length} File(s) Selected`}</Typography>
        {files.length > 0 && (
          <Typography variant='bodySmall' sx={{ ml: 2 }}>
            {formatBytes(totalSize)}
          </Typography>
        )}
      </Box>

      <Grid container sx={{ ...styles.dataGrid, backgroundColor: 'background.light' }}>
        {/* Column headers */}
        <Grid size={{ xs: 12 }} container>
          <Grid
            size={{ xs: 12 }}
            container
            sx={{
              margin: 0,
              paddingY: 0,
              paddingX: 1,
              borderBottom: '1px solid black',
              borderBottomColor: 'other.divider',
              columnGap: 2,
            }}
          >
            {[
              { label: 'File Name/Type', flex: 3 },
              { label: 'Size', flex: 1 },
              { label: 'Document Type', flex: 2 },
              { label: 'Description', flex: 2 },
              { label: '', flex: 0, width: 50 },
            ].map(({ label, flex, width }) => (
              <Grid key={label || 'action'} sx={{ flex, width }}>
                <Typography
                  variant='bodySmall'
                  className='MuiDataGrid-columnHeaderTitle'
                  sx={{ color: (theme) => `${theme.palette.text.main} !important` }}
                >
                  {label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Rows */}
          <Grid
            container
            size={{ xs: 12 }}
            sx={[
              { paddingX: 1, minHeight: '80px', maxHeight: '280px', overflow: 'auto' },
              files.length === 0 ? { alignContent: 'center', justifyContent: 'center' } : {},
            ]}
          >
            {files.length === 0 && (
              <Typography variant='bodySmall' sx={{ mb: 2 }}>
                No files selected
              </Typography>
            )}

            {files.map((entry, index) => (
              <Grid
                size={{ xs: 12 }}
                container
                key={`${entry.artifact.name}-${index}`}
                sx={{ alignItems: 'flex-end', columnGap: 2 }}
              >
                <Grid sx={{ flex: 3, alignSelf: 'center' }}>
                  <Box sx={{ my: 1 }}>
                    <AttachmentFile {...entry.artifact} />
                  </Box>
                </Grid>
                <Grid sx={{ flex: 1, alignSelf: 'center' }}>
                  <Typography variant='bodySmall'>{formatBytes(entry.artifact.size)}</Typography>
                </Grid>
                <Grid sx={{ flex: 2 }}>
                  <Box sx={{ my: 1 }}>
                    {entry.artifact.invalidMsg ? (
                      <Typography variant='bodySmall' sx={{ color: 'error.main' }}>
                        Cannot attach this file
                      </Typography>
                    ) : (
                      <TextField
                        select
                        label='Document Type'
                        size='small'
                        fullWidth
                        value={entry.documentTypeId}
                        onChange={(e) => onUpdateEntry(index, { documentTypeId: e.target.value as string })}
                        required
                      >
                        {uploadableTypes.map((dt) => (
                          <MenuItem key={dt.id} value={dt.id}>
                            {dt.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>
                </Grid>
                <Grid sx={{ flex: 2 }}>
                  <Box sx={{ my: 1 }}>
                    {!entry.artifact.invalidMsg && (
                      <TextField
                        variant='outlined'
                        placeholder='Description (optional)'
                        value={entry.description}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          e.preventDefault();
                          onUpdateEntry(index, { description: e.target.value });
                        }}
                        fullWidth
                        otherInputProps={{ inputProps: { maxLength: 200 } }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid sx={{ flex: 0, width: 50, alignSelf: 'center' }}>
                  <IconButton onClick={() => onRemoveFile(index)} sx={{ color: 'icon.dark' }} aria-label='Remove file'>
                    <TrashAlt />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Drop zone */}
        <Grid size={{ xs: 12 }}>
          <FileDropWrapper handleDragOver={onDragOver} handleDragLeave={onDragLeave} handleDrop={onDrop}>
            <Grid
              container
              direction='column'
              sx={[
                { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                (theme) => ({
                  width: 'auto',
                  border: `1px dashed ${theme.palette.secondary.main}`,
                  margin: 1,
                  color: 'text.main',
                  backgroundColor: getDropZoneBg(dragging, theme.palette),
                }),
              ]}
            >
              {uploadableTypes.length === 0 ? (
                <Typography variant='bodySmall' sx={{ margin: 1, color: 'text.subdued' }}>
                  No document types are available for upload.
                </Typography>
              ) : (
                <>
                  <Typography variant='h5' sx={{ margin: 1 }}>
                    Drag and Drop Files
                  </Typography>
                  <Typography variant='bodySmall' sx={{ textAlign: 'center', mb: 1 }}>
                    Drop files here or{' '}
                    <Button variant='text' sx={{ padding: 0, color: 'secondary.main' }} startIcon={<FileUpload />}>
                      <label htmlFor={fileInputId} style={{ cursor: 'pointer' }}>
                        <input
                          multiple
                          accept={acceptedFileTypes}
                          type='file'
                          id={fileInputId}
                          style={{ display: 'none' }}
                          onChange={onFileInputChange}
                          onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                            (event.target as HTMLInputElement).value = '';
                          }}
                        />
                        Browse Files
                      </label>
                    </Button>
                  </Typography>
                </>
              )}
            </Grid>
          </FileDropWrapper>
        </Grid>
      </Grid>
    </Grid>
  );
};
